import React, {useState, useEffect, useContext} from 'react'
import {useTranslation} from 'react-i18next'
import {EmailType} from '@common/models'
import {EnumType, useEnumTranslations} from '@common/utils'

import {
  StyledSelect,
  InputWrapper,
  TestEmailInputWrapper,
  TestInputContainer,
  ButtonsContainer,
  LeftButtonsContainer,
  TestButtonWrapper,
  HelpContent,
  HelpSection,
  PlaceholderList,
  PlaceholderItem,
} from './EmailTemplatesPage.styled'

import {Container} from '~/components/general/container/Container'
import {Typography} from '~/components/general/typography/Typography'
import {Button} from '~/components/general/button/Button'
import {Input} from '~/components/general/input/Input'
import {TextArea} from '~/components/general/textArea/TextArea'
import {Modal} from '~/components/general/modal/Modal'
import {
  useGetEmailTemplate,
  useUpdateEmailTemplate,
  useSendTestEmail,
} from '~/api/partnerAdministrator'
import {
  NotificationContext,
  NotificationType,
} from '~/components/notifications/NotificationContext'
import {isEmailValid} from '~/utils/general'

export const EmailTemplatesPage = () => {
  const {t} = useTranslation()
  const {addNotification} = useContext(NotificationContext)
  const {selectOptions} = useEnumTranslations()
  const [selectedEmailType, setSelectedEmailType] = useState<EmailType>(
    EmailType.CONFIRMATION,
  )
  const [subject, setSubject] = useState('')
  const [section1, setSection1] = useState('')
  const [section2, setSection2] = useState('')
  const [hasChanges, setHasChanges] = useState(false)
  const [showHelpModal, setShowHelpModal] = useState(false)
  const [showTestInput, setShowTestInput] = useState(false)
  const [testEmail, setTestEmail] = useState('')
  const [testEmailError, setTestEmailError] = useState<string | undefined>()

  const {data: template, isLoading} = useGetEmailTemplate(selectedEmailType)
  const {mutate: updateTemplate, isPending: isSaving} =
    useUpdateEmailTemplate(selectedEmailType)
  const {mutate: sendTestEmail, isPending: isSendingTest} =
    useSendTestEmail(selectedEmailType)

  const emailTypeOptions = selectOptions[EnumType.EmailType]

  // Update form fields when template data changes
  useEffect(() => {
    if (template) {
      setSubject(template.subject)
      setSection1(template.section1)
      setSection2(template.section2)
      setHasChanges(false)
    }
  }, [template])

  // Track changes
  useEffect(() => {
    if (template) {
      const changed =
        subject !== template.subject ||
        section1 !== template.section1 ||
        section2 !== template.section2
      setHasChanges(changed)
    }
  }, [subject, section1, section2, template])

  const handleSave = () => {
    if (!hasChanges || !selectedEmailType) return

    updateTemplate(
      {
        urlParams: {emailType: selectedEmailType},
        requestBody: {
          subject,
          section1,
          section2,
        },
      },
      {
        onSuccess: () => {
          addNotification(
            t('Email template updated successfully'),
            NotificationType.Success,
          )
          setHasChanges(false)
        },
        onError: (error: Error) => {
          addNotification(
            error.message || t('Failed to update email template'),
            NotificationType.Error,
          )
        },
      },
    )
  }

  const handleTestClick = () => {
    if (hasChanges) {
      addNotification(
        t('Please save changes before testing the template'),
        NotificationType.Error,
      )
      return
    }
    setShowTestInput(true)
  }

  const handleSendTestEmail = () => {
    if (!testEmail.trim()) {
      setTestEmailError(t('Email is required'))
      return
    }

    if (!isEmailValid(testEmail)) {
      setTestEmailError(t('Invalid email format'))
      return
    }

    setTestEmailError(undefined)

    sendTestEmail(
      {
        urlParams: {emailType: selectedEmailType},
        requestBody: {recipientEmail: testEmail},
      },
      {
        onSuccess: () => {
          addNotification(
            t('Test email sent successfully'),
            NotificationType.Success,
          )
          setTestEmail('')
          setShowTestInput(false)
        },
        onError: (error: Error) => {
          addNotification(
            error.message || t('Failed to send test email'),
            NotificationType.Error,
          )
        },
      },
    )
  }

  const handleEmailTypeChange = (newEmailType: EmailType) => {
    setSelectedEmailType(newEmailType)
    setHasChanges(false)
    setShowTestInput(false)
    setTestEmail('')
    setTestEmailError(undefined)
  }

  return (
    <Container flex direction="column" spacing={4}>
      <Container flex justify="space-between" align="center">
        <Typography size="lg" weight="medium">
          {t('Email templates')}
        </Typography>
      </Container>

      <Container direction="column" spacing={1}>
        <Typography>{t('Email type')}</Typography>
        <StyledSelect
          value={selectedEmailType}
          onChange={(e) => {
            handleEmailTypeChange(e.target.value as EmailType)
          }}>
          {emailTypeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </StyledSelect>
      </Container>

      {isLoading ? (
        <Container>
          <Typography>{t('Loading...')}</Typography>
        </Container>
      ) : (
        <>
          <Container direction="column" spacing={1}>
            <Typography>{t('Subject')}</Typography>
            <InputWrapper>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </InputWrapper>
          </Container>

          <Container direction="column" spacing={1}>
            <Typography>{t('Section 1')}</Typography>
            <TextArea
              rows={5}
              value={section1}
              onChange={(e) => setSection1(e.target.value)}
              style={{width: '100%', maxWidth: '800px'}}
            />
          </Container>

          <Container direction="column" spacing={1}>
            <Typography>{t('Section 2')}</Typography>
            <TextArea
              rows={5}
              value={section2}
              onChange={(e) => setSection2(e.target.value)}
              style={{width: '100%', maxWidth: '800px'}}
            />
          </Container>

          <ButtonsContainer flex>
            <LeftButtonsContainer flex>
              <Button size="sm" onClick={() => setShowHelpModal(true)}>
                {t('Help')}
              </Button>
              <TestButtonWrapper disabled={hasChanges}>
                <Button size="sm" onClick={handleTestClick}>
                  {t('Test')}
                </Button>
              </TestButtonWrapper>
            </LeftButtonsContainer>
            {showTestInput && (
              <TestInputContainer flex>
                <TestEmailInputWrapper>
                  <Input
                    type="email"
                    value={testEmail}
                    onChange={(e) => {
                      setTestEmail(e.target.value)
                      setTestEmailError(undefined)
                    }}
                    placeholder={t('Enter email address')}
                    errorMessage={testEmailError}
                  />
                </TestEmailInputWrapper>
                <Button
                  size="sm"
                  onClick={handleSendTestEmail}
                  disabled={isSendingTest || !testEmail.trim()}>
                  {isSendingTest ? t('Sending...') : t('Send test email')}
                </Button>
                <Button
                  size="sm"
                  color="secondary"
                  onClick={() => {
                    setShowTestInput(false)
                    setTestEmail('')
                    setTestEmailError(undefined)
                  }}>
                  {t('Cancel')}
                </Button>
              </TestInputContainer>
            )}
            <Button
              size="sm"
              onClick={handleSave}
              disabled={!hasChanges || isSaving}>
              {isSaving ? t('Saving...') : t('Save changes')}
            </Button>
          </ButtonsContainer>
        </>
      )}

      <Modal
        isVisible={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        header={t('Email Template Help')}
        footer="">
        <HelpContent>
          <HelpSection>
            <Typography weight="medium" size="md">
              {t('Markdown Formatting')}
            </Typography>
            <Typography size="sm">
              {t('You can use Markdown syntax to format your email templates:')}
            </Typography>
            <Container direction="column" spacing={1}>
              <Typography size="sm">
                <strong>{t('Headers:')}</strong> # {t('for large header')}, ##{' '}
                {t('for medium header')}, ### {t('for small header')}
              </Typography>
              <Typography size="sm">
                <strong>{t('Bold text:')}</strong> **{t('text')}**
              </Typography>
              <Typography size="sm">
                <strong>{t('Italic text:')}</strong> *{t('text')}*
              </Typography>
              <Typography size="sm">
                <strong>{t('Lists:')}</strong> - {t('item')}{' '}
                {t('for bullet points')}
              </Typography>
              <Typography size="sm">
                <strong>{t('Links:')}</strong> [{t('text')}]({t('url')})
              </Typography>
            </Container>
          </HelpSection>

          <HelpSection>
            <Typography weight="medium" size="md">
              {t('Available Placeholders')}
            </Typography>
            <Typography size="sm">
              {t(
                'You can use the following placeholders to personalize your email templates. They will be automatically replaced with actual values when the email is sent:',
              )}
            </Typography>
            <PlaceholderList>
              <PlaceholderItem>
                <strong>{'{{FIRST_NAME}}'}</strong> - {t('Patient first name')}
              </PlaceholderItem>
              <PlaceholderItem>
                <strong>{'{{SECOND_NAME}}'}</strong> -{' '}
                {t('Patient second name')}
              </PlaceholderItem>
              <PlaceholderItem>
                <strong>{'{{SERVICE_NAME}}'}</strong> - {t('Service name')}
              </PlaceholderItem>
              <PlaceholderItem>
                <strong>{'{{START_DATE}}'}</strong> -{' '}
                {t('Appointment start date')}
              </PlaceholderItem>
              <PlaceholderItem>
                <strong>{'{{START_TIME}}'}</strong> -{' '}
                {t('Appointment start time')}
              </PlaceholderItem>
              <PlaceholderItem>
                <strong>{'{{TIME_ZONE}}'}</strong> - {t('Time zone')}
              </PlaceholderItem>
            </PlaceholderList>
          </HelpSection>
        </HelpContent>
      </Modal>
    </Container>
  )
}
